import { useEffect, useMemo, useRef, useState } from "react";
import {
  deleteOldClientActivityLogs,
  fetchAdminComplexes,
  fetchClientActivityLogs,
} from "../../../admin/api";
import { adminStyles as styles } from "../shared/adminStyles";
import { formatDateTime, formatDurationMs, getCityName, normalizeSearchValue } from "../utils/adminFormatters";
import { buildClientWorkIntervals, getLogComplexId } from "../utils/historyHelpers";
import { useAdminTextSearch } from "../utils/useAdminTextSearch";
import { useCities } from "../../../providers/CitiesContext";
export default function ClientHistorySection() {
  const { cityNameByKey } = useCities();
  const [complexes, setComplexes] = useState([]);
  const [logs, setLogs] = useState([]);
  const [complexQuery, setComplexQuery] = useState("");
  const [selectedComplexId, setSelectedComplexId] = useState("");
  const [historyPeriod, setHistoryPeriod] = useState("current");
  const [isComboboxOpen, setIsComboboxOpen] = useState(false);
  const [isCleanupSaving, setIsCleanupSaving] = useState(false);
  const [cleanupMessage, setCleanupMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const searchBoxRef = useRef(null);
  const debouncedComplexQuery = useAdminTextSearch(complexQuery);

  const loadLogs = async () => {
    const logsData = await fetchClientActivityLogs({ period: historyPeriod });
    setLogs(Array.isArray(logsData) ? logsData : logsData?.items || []);
  };

  useEffect(() => {
    let active = true;

    async function loadData() {
      try {
        setIsLoading(true);
        setError("");

        const [complexesData, logsData] = await Promise.all([
          fetchAdminComplexes(),
          fetchClientActivityLogs({ period: historyPeriod }),
        ]);

        if (!active) return;

        setComplexes(Array.isArray(complexesData) ? complexesData : complexesData?.items || []);
        setLogs(Array.isArray(logsData) ? logsData : logsData?.items || []);
      } catch (loadError) {
        if (!active) return;

        setError(
          loadError instanceof Error
            ? loadError.message
            : "Не удалось загрузить историю"
        );
      } finally {
        if (active) setIsLoading(false);
      }
    }

    loadData();

    return () => {
      active = false;
    };
  }, [historyPeriod]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!searchBoxRef.current?.contains(event.target)) {
        setIsComboboxOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsComboboxOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const searchableComplexes = useMemo(() => {
    const normalizedQuery = normalizeSearchValue(debouncedComplexQuery.value);
    const sorted = [...complexes].sort((left, right) =>
      String(left?.name || "").localeCompare(String(right?.name || ""), "ru")
    );

    if (!normalizedQuery) {
      return sorted;
    }

    return sorted.filter((complex) =>
      normalizeSearchValue(complex?.name).includes(normalizedQuery)
    );
  }, [complexes, debouncedComplexQuery.value]);

  const selectedComplex = useMemo(
    () => complexes.find((complex) => String(complex?._id || "") === selectedComplexId) || null,
    [complexes, selectedComplexId]
  );

  const selectedLogs = useMemo(() => {
    if (!selectedComplex) {
      return [];
    }

    const complexId = String(selectedComplex._id || "");

    return logs.filter((log) => {
      const logComplexId = getLogComplexId(log);

      if (logComplexId && complexId) {
        return logComplexId === complexId;
      }

      return log?.complexName === selectedComplex.name;
    });
  }, [logs, selectedComplex]);

  const intervals = useMemo(() => buildClientWorkIntervals(selectedLogs), [selectedLogs]);

  const handleSelectComplex = (complex) => {
    setSelectedComplexId(String(complex?._id || ""));
    setComplexQuery(complex?.name || "");
    setIsComboboxOpen(false);
    setCleanupMessage("");
  };

  const handleResetComplex = () => {
    setSelectedComplexId("");
    setComplexQuery("");
    setIsComboboxOpen(false);
    setCleanupMessage("");
  };

  const handleCleanupOldLogs = async () => {
    if (!window.confirm("Будет удалена история старше предыдущего календарного месяца. Продолжить?")) {
      return;
    }

    try {
      setIsCleanupSaving(true);
      setError("");
      setCleanupMessage("");
      const cleanupResult = await deleteOldClientActivityLogs();
      await loadLogs();
      const deletedCount = Number(cleanupResult?.deletedCount || 0);
      setCleanupMessage(`Удалено записей: ${deletedCount}`);
    } catch (cleanupError) {
      setError(
        cleanupError instanceof Error
          ? cleanupError.message
          : "Не удалось очистить старую историю"
      );
    } finally {
      setIsCleanupSaving(false);
    }
  };

  return (
    <>
      <section style={styles.card}>
        <div style={styles.filterActionsBar}>
          <div>
            <p style={styles.placeholderText}>
              Найдите ЖК и сразу смотрите промежутки работы его клиентов.
            </p>
          </div>

          <div style={styles.actionsRow}>
            <div style={styles.viewControls}>
              <button
                type="button"
                style={{
                  ...styles.viewButton,
                  ...(historyPeriod === "current" ? styles.viewButtonActive : {}),
                }}
                onClick={() => {
                  setHistoryPeriod("current");
                  setCleanupMessage("");
                }}
              >
                Этот месяц
              </button>

              <button
                type="button"
                style={{
                  ...styles.viewButton,
                  ...(historyPeriod === "previous" ? styles.viewButtonActive : {}),
                }}
                onClick={() => {
                  setHistoryPeriod("previous");
                  setCleanupMessage("");
                }}
              >
                Предыдущий месяц
              </button>
            </div>

            <p style={styles.resultCount}>
              {cleanupMessage || (selectedComplex ? `Промежутков: ${intervals.length}` : "ЖК не выбран")}
            </p>

            <button
              type="button"
              disabled={isCleanupSaving}
              style={styles.viewButton}
              onClick={handleCleanupOldLogs}
            >
              {isCleanupSaving ? "Очищается..." : "Очистить старую историю"}
            </button>
          </div>
        </div>

        <div
          ref={searchBoxRef}
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "8px",
            flexWrap: "wrap",
          }}
        >
          <div style={{ position: "relative", flex: "1 1 280px", minWidth: "240px" }}>
            <input
              placeholder="Найти ЖК"
              style={{ ...styles.input, width: "100%" }}
              value={complexQuery}
              onChange={(event) => {
                setComplexQuery(event.target.value);
                setSelectedComplexId("");
                setIsComboboxOpen(true);
                setCleanupMessage("");
              }}
              onFocus={() => setIsComboboxOpen(true)}
            />

            {isComboboxOpen && !selectedComplex ? (
              <div
                style={{
                  position: "absolute",
                  zIndex: 20,
                  top: "calc(100% + var(--admin-gap-sm))",
                  left: 0,
                  right: 0,
                  maxHeight: "min(240px, 36dvh)",
                  overflowY: "auto",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--admin-radius)",
                  background: "var(--card-bg)",
                  boxShadow: "var(--shadow-panel)",
                }}
              >
                {searchableComplexes.length === 0 ? (
                  <div style={{ ...styles.state, minHeight: 0 }}>ЖК не найдены</div>
                ) : (
                  searchableComplexes.map((complex) => (
                    <button
                      key={complex._id}
                      type="button"
                      style={{
                        width: "100%",
                        border: "none",
                        borderBottom: "1px solid var(--border)",
                        background: "transparent",
                        color: "var(--text-main)",
                        cursor: "pointer",
                        padding: "var(--admin-control-padding-y) var(--admin-control-padding-x)",
                        textAlign: "left",
                      }}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => handleSelectComplex(complex)}
                    >
                      <div style={styles.name}>{complex.name || "Без названия"}</div>
                      <div style={styles.muted}>{getCityName(complex.city, cityNameByKey)}</div>
                    </button>
                  ))
                )}
              </div>
            ) : null}
          </div>

          {selectedComplex ? (
            <button
              type="button"
              style={{ ...styles.viewButton, flex: "0 0 auto" }}
              onClick={handleResetComplex}
            >
              Сбросить выбор
            </button>
          ) : null}
        </div>
      </section>

      <section style={styles.documentsCard}>
        <div style={styles.documentsHeader}>
          <h2 style={styles.documentsTitle}>
            {selectedComplex ? selectedComplex.name || "Без названия" : "Таймлайн ЖК"}
          </h2>
          <span style={styles.muted}>
            {selectedComplex ? `${selectedLogs.length} событий` : "выберите ЖК"}
          </span>
        </div>

        <div style={styles.documentsScroll}>
          {isLoading ? (
            <div style={styles.state}>Загрузка истории...</div>
          ) : error ? (
            <div style={styles.state}>{error}</div>
          ) : !selectedComplex ? (
            <div style={styles.state}>Найдите и выберите ЖК, чтобы открыть таймлайн клиентов</div>
          ) : selectedLogs.length === 0 ? (
            <div style={styles.state}>Для этого ЖК пока нет событий истории</div>
          ) : intervals.length === 0 ? (
            <div style={styles.state}>Для этого ЖК пока нет завершенных или открытых промежутков</div>
          ) : (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <colgroup>
                  <col style={{ width: "18%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "16%" }} />
                  <col style={{ width: "16%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "12%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th style={styles.th}>Клиент</th>
                    <th style={styles.th}>СИП</th>
                    <th style={styles.th}>Номер перевода</th>
                    <th style={styles.th}>Начало работы</th>
                    <th style={styles.th}>Конец работы</th>
                    <th style={styles.th}>Длительность</th>
                    <th style={styles.th}>Статус</th>
                  </tr>
                </thead>

                <tbody>
                  {intervals.map((interval, index) => {
                    const now = Date.now();
                    const endTime = interval.endTime || now;
                    const duration = formatDurationMs(endTime - interval.startTime);
                    const rowBackground =
                      index % 2 === 0 ? "var(--card-bg)" : "var(--card-bg-soft)";

                    return (
                      <tr
                        key={`${interval.sipClientId || interval.clientName}-${interval.startedAt}-${interval.endedAt || "open"}-${index}`}
                        style={{ background: rowBackground }}
                        onMouseEnter={(event) => {
                          event.currentTarget.style.background = "var(--accent-soft)";
                        }}
                        onMouseLeave={(event) => {
                          event.currentTarget.style.background = rowBackground;
                        }}
                      >
                        <td style={styles.td}>{interval.clientName || "—"}</td>
                        <td style={styles.td}>{interval.sip_number || "—"}</td>
                        <td style={styles.td}>{interval.transfer_number || "—"}</td>
                        <td style={styles.td}>{formatDateTime(interval.startedAt)}</td>
                        <td style={styles.td}>
                          {interval.endedAt ? formatDateTime(interval.endedAt) : "Работает сейчас"}
                        </td>
                        <td style={styles.td}>{duration}</td>
                        <td style={styles.td}>
                          <span
                            style={{
                              ...styles.badge,
                              ...(interval.isOpen ? styles.activeBadge : styles.stopBadge),
                            }}
                          >
                            {interval.isOpen ? "Работает сейчас" : "Завершено"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
